import { io, Socket } from 'socket.io-client';
import { apiFetch } from '@/api/client';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

/**
 * Dashboard auth is server-managed (HttpOnly `ghq_sid` cookie + JWT minted
 * by /api/auth). The Supabase client never holds a session, so calling
 * `supabase.auth.getSession()` here always returned null. Instead we fetch
 * a short-lived JWT from /api/auth/ws-token, which uses the cookie session
 * to authenticate the request and signs a JWT that the gateway accepts.
 */
async function fetchWsToken(): Promise<string | null> {
  try {
    const res = await apiFetch<{ success: boolean; data?: { token: string; expiresIn: number } }>(
      '/api/auth/ws-token',
    );
    return res.data?.token ?? null;
  } catch {
    return null;
  }
}

let socket: Socket | null = null;
let currentRestaurantId: string | null = null;
let pendingPromise: Promise<Socket> | null = null;

/**
 * Returns a shared socket.io connection scoped to a single restaurantId.
 *
 * Caches the in-flight Promise so concurrent callers (e.g. Sidebar's
 * useOrderCounts + OrdersPage's WS effect) all await the SAME connection
 * attempt. Previously, a second call while the first was still
 * mid-handshake saw `socket.connected === false`, tore the connecting
 * socket down, and started another. With socket.io's infinite reconnect
 * this produced the 101 → 200ms → drop → repeat storm.
 */
export async function getSocket(restaurantId: string): Promise<Socket> {
  // Already connected for this restaurant → reuse.
  if (socket?.connected && currentRestaurantId === restaurantId) return socket;

  // Connection in flight for this restaurant → reuse the same promise.
  if (pendingPromise && currentRestaurantId === restaurantId) return pendingPromise;

  // Switching restaurants, or stale dead socket → tear down before reconnecting.
  if (socket) {
    socket.disconnect();
    socket = null;
  }

  currentRestaurantId = restaurantId;

  pendingPromise = (async () => {
    const token = await fetchWsToken();

    if (!token) {
      pendingPromise = null;
      throw new Error('WS_NO_SESSION');
    }

    const url = `${API_BASE}/ws/orders`;
    console.info(`[ws] connecting to ${url} for restaurant ${restaurantId}`);

    const s = io(url, {
      auth: { token },
      query: { restaurantId },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
    });

    s.on('connect',       () => console.info(`[ws] connected (${s.id})`));
    s.on('disconnect',    (reason) => console.info(`[ws] disconnected: ${reason}`));
    s.on('connect_error', (err) => console.warn(`[ws] connect_error: ${err.message}`));

    // Server emits `auth_error` (see OrdersGateway.rejectClient) and then closes.
    // Stop reconnecting — retries will keep failing for the same reason.
    s.on('auth_error', (payload: { code?: string; message?: string }) => {
      console.error('[ws] auth rejected:', payload);
      s.disconnect();
      if (socket === s) {
        socket = null;
        currentRestaurantId = null;
      }
    });

    s.io.on('reconnect_failed', () => {
      console.error('[ws] reconnect attempts exhausted — giving up');
      if (socket === s) {
        socket = null;
        currentRestaurantId = null;
      }
    });

    socket = s;
    pendingPromise = null;
    return s;
  })();

  return pendingPromise;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
  currentRestaurantId = null;
  pendingPromise = null;
}
